import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { Ticket, Plus, Trash2, Copy, Calendar, Users } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

type CouponType = "plan" | "credits" | "storage";

interface CouponForm {
  code: string;
  coupon_type: CouponType;
  plan_type: string;
  plan_duration_days: number;
  credits_amount: number;
  storage_amount: number;
  max_uses: number;
  expires_at: string;
}

const defaultForm: CouponForm = {
  code: "",
  coupon_type: "plan",
  plan_type: "basic",
  plan_duration_days: 30,
  credits_amount: 100000,
  storage_amount: 1073741824,
  max_uses: 1,
  expires_at: "",
};

const generateCode = () => {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let code = "JUS-";
  for (let i = 0; i < 8; i++) code += chars[Math.floor(Math.random() * chars.length)];
  return code;
};

const formatBytes = (bytes: number) => {
  if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(0)} GB`;
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(0)} MB`;
  return `${bytes} bytes`;
};

const formatCredits = (n: number) => {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(0)}K`;
  return `${n}`;
};

export default function CouponsTab() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<CouponForm>(defaultForm);

  const { data: coupons = [], isLoading } = useQuery({
    queryKey: ["admin-coupons"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("coupons")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (formData: CouponForm) => {
      const payload: any = {
        code: formData.code.toUpperCase().trim(),
        coupon_type: formData.coupon_type,
        max_uses: formData.max_uses,
        is_active: true,
        created_by: user?.id,
      };

      if (formData.expires_at) payload.expires_at = new Date(formData.expires_at).toISOString();

      if (formData.coupon_type === "plan") {
        payload.plan_type = formData.plan_type;
        payload.plan_duration_days = formData.plan_duration_days;
      } else if (formData.coupon_type === "credits") {
        payload.credits_amount = formData.credits_amount;
      } else if (formData.coupon_type === "storage") {
        payload.storage_amount = formData.storage_amount;
      }

      const { error } = await supabase.from("coupons").insert(payload);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-coupons"] });
      setShowForm(false);
      setForm(defaultForm);
      toast({ title: "Cupom criado com sucesso!" });
    },
    onError: (e: any) => {
      toast({ title: "Erro ao criar cupom", description: e.message, variant: "destructive" });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase.from("coupons").update({ is_active }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-coupons"] }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("coupons").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-coupons"] });
      toast({ title: "Cupom excluído" });
    },
  });

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    toast({ title: "Código copiado!" });
  };

  const typeLabel = (type: string) => {
    switch (type) {
      case "plan": return "Plano";
      case "credits": return "Créditos";
      case "storage": return "Armazenamento";
      default: return type;
    }
  };

  const typeColor = (type: string) => {
    switch (type) {
      case "plan": return "bg-blue-600";
      case "credits": return "bg-amber-600";
      case "storage": return "bg-emerald-600";
      default: return "bg-gray-600";
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Ticket className="h-5 w-5" />
              Gestão de Cupons
            </CardTitle>
            <CardDescription>Crie e gerencie cupons para planos, créditos e armazenamento</CardDescription>
          </div>
          <Button onClick={() => { setShowForm(!showForm); setForm({ ...defaultForm, code: generateCode() }); }}>
            <Plus className="h-4 w-4 mr-2" />
            Novo Cupom
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Form */}
        {showForm && (
          <Card className="border-dashed border-2 border-blue-300 bg-blue-50/50">
            <CardContent className="p-6 space-y-4">
              <h3 className="font-semibold text-lg">Criar Novo Cupom</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Código do Cupom</Label>
                  <div className="flex gap-2">
                    <Input
                      value={form.code}
                      onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                      placeholder="JUS-XXXXXXXX"
                    />
                    <Button variant="outline" size="icon" onClick={() => setForm({ ...form, code: generateCode() })}>
                      🎲
                    </Button>
                  </div>
                </div>

                <div>
                  <Label>Tipo de Cupom</Label>
                  <Select value={form.coupon_type} onValueChange={(v) => setForm({ ...form, coupon_type: v as CouponType })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="plan">📋 Plano</SelectItem>
                      <SelectItem value="credits">🪙 Créditos</SelectItem>
                      <SelectItem value="storage">💾 Armazenamento</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Conditional fields */}
              {form.coupon_type === "plan" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Plano</Label>
                    <Select value={form.plan_type} onValueChange={(v) => setForm({ ...form, plan_type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="basic">Básico (R$44,50/mês)</SelectItem>
                        <SelectItem value="pro">Pro (R$74,50/mês)</SelectItem>
                        <SelectItem value="premium">Premium (R$124,50/mês)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Duração (dias)</Label>
                    <Input
                      type="number"
                      value={form.plan_duration_days}
                      onChange={(e) => setForm({ ...form, plan_duration_days: parseInt(e.target.value) || 30 })}
                    />
                  </div>
                </div>
              )}

              {form.coupon_type === "credits" && (
                <div>
                  <Label>Quantidade de Créditos</Label>
                  <Select
                    value={String(form.credits_amount)}
                    onValueChange={(v) => setForm({ ...form, credits_amount: parseInt(v) })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="100000">100K créditos (R$15)</SelectItem>
                      <SelectItem value="200000">200K créditos (R$26)</SelectItem>
                      <SelectItem value="600000">600K créditos (R$69)</SelectItem>
                      <SelectItem value="500000">500K créditos (personalizado)</SelectItem>
                      <SelectItem value="1000000">1M créditos (personalizado)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {form.coupon_type === "storage" && (
                <div>
                  <Label>Quantidade de Armazenamento</Label>
                  <Select
                    value={String(form.storage_amount)}
                    onValueChange={(v) => setForm({ ...form, storage_amount: parseInt(v) })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1073741824">1 GB</SelectItem>
                      <SelectItem value="5368709120">5 GB</SelectItem>
                      <SelectItem value="10737418240">10 GB</SelectItem>
                      <SelectItem value="53687091200">50 GB</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Máximo de usos</Label>
                  <Input
                    type="number"
                    value={form.max_uses}
                    onChange={(e) => setForm({ ...form, max_uses: parseInt(e.target.value) || 1 })}
                    min={1}
                  />
                </div>
                <div>
                  <Label>Expira em (opcional)</Label>
                  <Input
                    type="datetime-local"
                    value={form.expires_at}
                    onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button onClick={() => createMutation.mutate(form)} disabled={!form.code || createMutation.isPending}>
                  {createMutation.isPending ? "Criando..." : "Criar Cupom"}
                </Button>
                <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* List */}
        {isLoading ? (
          <p className="text-center text-muted-foreground py-8">Carregando cupons...</p>
        ) : coupons.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">Nenhum cupom criado ainda.</p>
        ) : (
          <div className="space-y-3">
            {coupons.map((coupon: any) => (
              <div
                key={coupon.id}
                className="flex items-center justify-between p-4 rounded-lg border bg-white shadow-sm"
              >
                <div className="flex items-center gap-4">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <code className="font-mono font-bold text-lg">{coupon.code}</code>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyCode(coupon.code)}>
                        <Copy className="h-3 w-3" />
                      </Button>
                      <Badge className={`${typeColor(coupon.coupon_type)} text-white text-xs`}>
                        {typeLabel(coupon.coupon_type)}
                      </Badge>
                      {!coupon.is_active && <Badge variant="secondary">Inativo</Badge>}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      {coupon.coupon_type === "plan" && (
                        <span>Plano: <strong>{coupon.plan_type}</strong> ({coupon.plan_duration_days} dias)</span>
                      )}
                      {coupon.coupon_type === "credits" && (
                        <span>Créditos: <strong>{formatCredits(coupon.credits_amount)}</strong></span>
                      )}
                      {coupon.coupon_type === "storage" && (
                        <span>Storage: <strong>{formatBytes(coupon.storage_amount)}</strong></span>
                      )}
                      <span className="flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        {coupon.current_uses}/{coupon.max_uses} usos
                      </span>
                      {coupon.expires_at && (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Expira: {format(new Date(coupon.expires_at), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Switch
                    checked={coupon.is_active}
                    onCheckedChange={(checked) => toggleMutation.mutate({ id: coupon.id, is_active: checked })}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-red-500 hover:text-red-700"
                    onClick={() => deleteMutation.mutate(coupon.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
